#!/usr/bin/env node

/**
 * @sailfish-ai/autofix CLI
 *
 * Universal installer for Sailfish Autofix across 30+ AI coding agents.
 * Installs the Agent Skill (open standard) and configures the MCP server.
 */

import { Command } from "commander";
import { readFileSync } from "fs";
import { fileURLToPath } from "url";
import { dirname, join } from "path";

const __filename = fileURLToPath(import.meta.url);
const __dirname = dirname(__filename);

// Version comes from package.json (the single source of truth). This is only a
// sentinel for the unreachable case where that read fails — keep it as 0.0.0 so it
// can never silently go stale behind the real version again.
let version = "0.0.0";
try {
  const pkg = JSON.parse(readFileSync(join(__dirname, "../..", "package.json"), "utf-8"));
  version = pkg.version;
} catch {
  // Use default version
}

const program = new Command();

program
  .name("sailfish-autofix")
  .description(
    "Sailfish Autofix — Install the Autofix skill and MCP server across AI coding agents.\n\n" +
    "Supports Claude Code, Cursor, Codex, Copilot, OpenCode, Windsurf, Gemini CLI, Goose, and 30+ more."
  )
  .version(version);

program
  .command("setup")
  .description("All-in-one: authenticate, detect agents, install skill + MCP config")
  .option("--token <token>", "Use a pre-authenticated token (from Sailfish web app)")
  .option("--graphql-url <url>", "GraphQL endpoint the token was issued for (defaults to production)")
  .option("--global", "Install skill globally (for all projects)")
  .option("--agents <agents>", "Comma-separated list of agents to configure (e.g. claude-code,cursor)")
  .option("-y, --yes", "Skip confirmation prompts")
  .action(async (opts) => {
    const { runSetup } = await import("../src/cli/setup.js");
    await runSetup(opts);
  });

program
  .command("uninstall")
  .description("Remove all installed skills, MCP configs, and optionally credentials")
  .option("--keep-credentials", "Don't remove authentication credentials")
  .option("-y, --yes", "Skip confirmation prompts")
  .action(async (opts) => {
    const { runUninstall } = await import("../src/cli/uninstall.js");
    await runUninstall(opts);
  });

program
  .command("login")
  .description("Authenticate with Sailfish (OAuth browser flow)")
  .option("--token <token>", "Use a pre-authenticated token instead of browser flow")
  .option("--graphql-url <url>", "GraphQL endpoint the token was issued for (defaults to production)")
  .action(async (opts) => {
    const { runLogin } = await import("../src/cli/login.js");
    await runLogin(opts);
  });

program
  .command("logout")
  .description("Remove stored credentials")
  .action(async () => {
    const { runLogout } = await import("../src/cli/login.js");
    await runLogout();
  });

program
  .command("status")
  .description("Show authentication status and installed components")
  .action(async () => {
    const { runStatus } = await import("../src/cli/status.js");
    await runStatus();
  });

program
  .command("serve")
  .description("Run as MCP server (STDIO mode — used by agent configurations)")
  .action(async () => {
    const { runServe } = await import("../src/cli/serve.js");
    await runServe();
  });

program
  .command("fork <issue-id>")
  .description("Download the latest autofix conversation for an issue and resume it locally")
  .option("--cwd <path>", "Override working directory (default: current git root)")
  .option("--agent <agent>", "Agent to resume in: claude-code (default), codex, cursor, opencode, goose, gemini, windsurf, copilot")
  .option("--native", "opencode/gemini only: use the native import/checkpoint path (higher fidelity, but pinned to the tool's version) instead of the default portable RESUME.md package")
  .option("--token <token>", "Use a pre-authenticated token (from Sailfish web app) — authenticates before resuming")
  .option("--graphql-url <url>", "GraphQL endpoint the token was issued for (defaults to production)")
  .option("--company-id <id>", "Company that owns the run (internal cross-company resume; included by the Sailfish web app command)")
  .action(async (issueId: string, opts: { cwd?: string; agent?: string; native?: boolean; token?: string; graphqlUrl?: string; companyId?: string }) => {
    const { runFork } = await import("../src/cli/fork.js");
    await runFork(issueId, opts);
  });

program
  .command("doctor")
  .description("Diagnose issues with auth, MCP server, and agent configurations")
  .action(async () => {
    const { runDoctor } = await import("../src/cli/doctor.js");
    await runDoctor();
  });

program.parse();
